import React, { useState, useContext, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faReply, 
  faEdit, 
  faTrash, 
  faUser, 
  faSpinner, 
  faHeart, 
  faEllipsisV, 
  faChevronUp, 
  faChevronDown, 
  faExclamationCircle, 
  faExclamationTriangle, 
  faExternalLinkAlt, 
  faThumbtack, 
  faShieldAlt, 
  faLink, 
  faCheck,
  faThumbsUp,
  faThumbsDown,
  faFlag
} from '@fortawesome/free-solid-svg-icons';
import { AuthContext } from '../../context/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { formatLocalDateTime } from '../../utils/formatters';
import { processEmojis } from '../../utils/emojiUtils';
import CommentForm from './CommentForm';
import { useNavigate } from 'react-router-dom';
import { CommentsAPI } from '../../api';

// Define animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(10px);
  }
`;

// Add error notification animation
const shake = keyframes`
  0% { transform: translateX(0); }
  20% { transform: translateX(-5px); }
  40% { transform: translateX(5px); }
  60% { transform: translateX(-5px); }
  80% { transform: translateX(5px); }
  100% { transform: translateX(0); }
`;

// Add a toast notification for link copying
const fadeInOut = keyframes`
  0% { opacity: 0; transform: translateY(10px); }
  10% { opacity: 1; transform: translateY(0); }
  90% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-10px); }
`;

// Add an error message component
const ErrorMessage = styled.div`
  background-color: rgba(220, 38, 38, 0.1);
  color: #ff4d4d;
  padding: 10px;
  border-radius: 8px;
  margin: 10px 0;
  display: flex;
  align-items: center;
  font-size: 13px;
  animation: ${shake} 0.5s ease-in-out;
  border: 1px solid rgba(220, 38, 38, 0.2);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  
  svg {
    margin-right: 8px;
    font-size: 14px;
  }
`;

const CommentContainer = styled.div`
  margin-bottom: 15px;
  max-width: 100%;
  animation: ${fadeIn} 0.3s ease-out;
  transition: all 0.3s ease;
  padding: 10px 0;
  
  &.deleting {
    animation: ${fadeOut} 0.3s ease-out forwards;
  }
  
  @media (max-width: 768px) {
    padding: 10px 0;
    margin-bottom: 5px;
  }
`;

const CommentHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  position: relative;
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 12px;
  border: 2px solid rgba(59, 130, 246, 0.5);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
    border-color: var(--primary-light);
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  svg {
    width: 100%;
    height: 100%;
    padding: 8px;
    color: var(--text-light);
    background-color: rgba(59, 130, 246, 0.2);
  }
  
  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    margin-right: 10px;
    border-width: 1.5px;
  }
`;

const CommentMeta = styled.div`
  flex: 1;
`;

const Username = styled.div`
  font-weight: 600;
  font-size: 15px;
  color: var(--text-light);
  cursor: pointer;
  transition: color 0.2s ease;
  display: inline-block;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  
  &:hover {
    color: var(--primary-light);
    text-decoration: underline;
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.2;
  }
`;

const Timestamp = styled.div`
  font-size: 12px;
  color: var(--text-dim);
  cursor: help;
  
  &:hover {
    text-decoration: underline;
    color: var(--primary-light);
  }
  
  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const Content = styled.div`
  color: var(--text-light);
  font-size: 14px;
  line-height: 1.5;
  margin: 5px 0 10px;
  word-break: break-word;
  white-space: pre-wrap; /* Preserve line breaks and spaces */
  
  @media (max-width: 768px) {
    font-size: 13px;
    line-height: 1.4;
    margin-bottom: 8px;
  }
  
  a {
    color: var(--primary-light);
    text-decoration: underline;
    position: relative;
    padding-right: 18px;
    transition: all 0.2s ease;
    
    &:hover {
      color: var(--primary-color);
    }
    
    &.external-link::after {
      content: "";
      position: absolute;
      right: 0;
      top: 2px;
      width: 14px;
      height: 14px;
      background-color: currentColor;
      mask-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M432,320H400a16,16,0,0,0-16,16V448H64V128H208a16,16,0,0,0,16-16V80a16,16,0,0,0-16-16H48A48,48,0,0,0,0,112V464a48,48,0,0,0,48,48H400a48,48,0,0,0,48-48V336A16,16,0,0,0,432,320ZM488,0h-128c-21.37,0-32.05,25.91-17,41l35.73,35.73L135,320.37a24,24,0,0,0,0,34L157.67,377a24,24,0,0,0,34,0L435.28,133.32,471,169c15,15,41,4.5,41-17V24A24,24,0,0,0,488,0Z"/></svg>');
      mask-repeat: no-repeat;
      mask-size: contain;
    }
  }
  
  img.inline-emoji {
    width: 22px;
    height: 22px;
    vertical-align: middle;
    margin: 0 2px;
    display: inline-block;
    position: relative;
    top: -2px;
    
    @media (max-width: 768px) {
      width: 18px;
      height: 18px;
      top: -1px;
    }
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  font-size: 13px;
  margin-left: 10px;
  
  @media (max-width: 768px) {
    font-size: 12px;
    gap: 8px;
    margin-left: 5px;
  }
`;

const ActionButton = styled.button`
  background: transparent;
  border: none;
  color: var(--text-dim);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  padding: 5px 8px;
  border-radius: 4px;
  
  &:hover {
    color: var(--primary-light);
    transform: translateY(-1px);
    background-color: rgba(255, 255, 255, 0.05);
  }
  
  svg {
    margin-right: 5px;
    font-size: 14px;
  }
  
  &.delete:hover {
    color: var(--error-color);
  }
  
  @media (max-width: 768px) {
    padding: 5px;
    
    .button-text {
      display: none;
    }
    
    svg {
      margin-right: 0;
      font-size: 15px;
    }
  }
`;

const MobileActionsButton = styled.button`
  display: none;
  background: transparent;
  border: none;
  color: var(--text-dim);
  padding: 5px;
  border-radius: 50%;
  cursor: pointer;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: var(--text-light);
  }
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    right: 0;
    top: 0;
  }
`;

const MobileActionsMenu = styled.div`
  position: absolute;
  right: 0;
  top: 35px;
  background-color: rgba(26, 26, 26, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  z-index: 1010;
  overflow: hidden;
  min-width: 140px;
  
  button {
    display: flex;
    align-items: center;
    padding: 10px 15px;
    width: 100%;
    text-align: left;
    background: transparent;
    border: none;
    color: var(--text-light);
    font-size: 14px;
    
    &:not(:last-child) {
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    }
    
    &:hover {
      background-color: rgba(255, 255, 255, 0.05);
    }
    
    svg {
      margin-right: 10px;
      width: 16px;
    }
    
    &.delete {
      color: var(--error-color);
    }
  }
`;

const RepliesContainer = styled.div`
  margin-left: 25px;
  margin-top: 10px;
  padding-left: 15px;
  border-left: 2px solid rgba(150, 150, 150, 0.2);
  transition: all 0.3s ease;
  
  /* Add different border colors for different nesting levels */
  &:nth-child(odd) {
    border-left-color: rgba(150, 150, 150, 0.2);
  }
  
  &:nth-child(even) {
    border-left-color: rgba(150, 150, 150, 0.2);
  }
  
  /* Add visual indicator for deeply nested comments */
  & > div {
    position: relative;
    
    &:before {
      display: none; /* Hide the small line in replies */
    }
  }
  
  @media (max-width: 768px) {
    margin-left: 10px;
    padding-left: 10px;
    margin-top: 8px;
    border-left-width: 1.5px;
  }
`;

const ReplyFormContainer = styled.div`
  margin-top: 15px;
  margin-bottom: 15px;
  margin-left: 25px;
  
  @media (max-width: 768px) {
    margin-left: 10px;
    margin-top: 10px;
    margin-bottom: 10px;
  }
`;

const RelativeTime = styled.span`
  font-size: 12px;
  color: var(--text-dim);
  margin-left: 5px;
  
  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const ReplyingTo = styled.div`
  background-color: rgba(59, 130, 246, 0.1);
  color: var(--text-dim);
  padding: 8px 12px;
  border-radius: 8px;
  margin-bottom: 10px;
  font-size: 13px;
  
  span {
    color: var(--primary-light);
    font-weight: 500;
  }
`;

const ToggleRepliesButton = styled.button`
  background-color: transparent;
  border: none;
  color: var(--text-dim);
  font-size: 13px;
  padding: 6px 10px;
  margin-top: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  border-radius: 4px;
  
  &:hover {
    color: var(--primary-light);
    background-color: rgba(255, 255, 255, 0.05);
  }
  
  svg {
    margin-right: 5px;
    font-size: 12px;
  }
`;

const ReplyCount = styled.span`
  background-color: rgba(59, 130, 246, 0.1);
  color: var(--primary-light);
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  margin-left: 5px;
  font-weight: 500;
`;

const ReplyChain = styled.div`
  display: flex;
  align-items: center;
  color: var(--text-dim);
  font-size: 13px;
  margin-bottom: 8px;
  
  svg {
    margin: 0 6px;
    font-size: 10px;
    color: var(--primary-light);
  }
  
  span {
    font-weight: 600;
    color: var(--primary-color);
    cursor: pointer;
    
    &:hover {
      text-decoration: underline;
      color: var(--primary-light);
    }
  }
`;

// Add a styled component for inline editing on mobile
const InlineEditContainer = styled.div`
  margin-top: 10px;
`;

// Add a modal component for external link warnings
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(3px);
`;

const ModalContent = styled.div`
  background-color: rgba(26, 26, 26, 0.95);
  border-radius: 12px;
  padding: 20px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(59, 130, 246, 0.3);
  
  h3 {
    color: var(--error-color);
    margin-bottom: 15px;
    display: flex;
    align-items: center;
    font-size: 18px;
    
    svg {
      margin-right: 10px;
    }
  }
  
  p {
    margin-bottom: 20px;
    color: var(--text-light);
    font-size: 14px;
    line-height: 1.5;
  }
  
  .link-display {
    background-color: rgba(0, 0, 0, 0.2);
    padding: 8px 12px;
    border-radius: 6px;
    margin-bottom: 15px;
    word-break: break-all;
    font-family: monospace;
    font-size: 13px;
    color: var(--primary-light);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  .button-group {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }
  
  button {
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &.cancel {
      background-color: transparent;
      border: 1px solid rgba(255, 255, 255, 0.2);
      color: var(--text-light);
      
      &:hover {
        background-color: rgba(255, 255, 255, 0.1);
      }
    }
    
    &.proceed {
      background-color: var(--primary-color);
      border: none;
      color: white;
      
      &:hover {
        background-color: var(--primary-light);
        transform: translateY(-1px);
      }
    }
  }
`;

const EmbeddedImage = styled.div`
  margin: 10px 0;
  max-width: 100%;
  
  img {
    max-width: 100%;
    max-height: 300px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  .image-caption {
    font-size: 12px;
    color: var(--text-dim);
    margin-top: 5px;
    display: flex;
    align-items: center;
    
    svg {
      margin-right: 5px;
    }
    
    a {
      color: var(--primary-light);
      margin-left: 5px;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

// Add styled components for pinned comments and moderator badges
const PinnedBadge = styled.div`
  display: flex;
  align-items: center;
  background-color: rgba(59, 130, 246, 0.1);
  color: var(--primary-light);
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 8px;
  font-weight: 600;
  
  svg {
    margin-right: 4px;
    font-size: 10px;
  }
`;

const ModeratorBadge = styled.div`
  display: flex;
  align-items: center;
  background-color: rgba(124, 58, 237, 0.1);
  color: #a78bfa;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 8px;
  font-weight: 600;
  
  svg {
    margin-right: 4px;
    font-size: 10px;
  }
`;

const CopyLinkToast = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 10px 20px;
  border-radius: 20px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 1000;
  animation: ${fadeInOut} 3s ease-in-out forwards;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  
  svg {
    color: var(--success-color);
  }
`;

// Toast for feedback messages
const FeedbackToast = styled(CopyLinkToast)`
  svg {
    color: ${props => {
      switch (props.$type) {
        case 'success': return 'var(--success-color)';
        case 'warning': return 'var(--warning-color)';
        case 'error': return 'var(--error-color)';
        default: return 'var(--primary-light)';
      }
    }};
  }
`;

// Add styled components for likes, dislikes, and reports
const VoteContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 5px;
  margin-bottom: 0;
`;

const VoteButton = styled.button`
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$active ? 'var(--primary-light)' : 'var(--text-dim)'};
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
    color: ${props => props.$active ? 'var(--primary-light)' : 'var(--text-light)'};
  }
  
  svg {
    margin-right: 4px;
    font-size: 14px;
  }
  
  @media (max-width: 768px) {
    font-size: 12px;
    padding: 4px 6px;
    
    svg {
      font-size: 13px;
      margin-right: 3px;
    }
  }
`;

const LikeButton = styled(VoteButton)`
  &:hover {
    color: ${props => props.$active ? 'var(--primary-light)' : 'var(--primary-light)'};
  }
`;

const DislikeButton = styled(VoteButton)`
  &:hover {
    color: ${props => props.$active ? 'var(--error-color)' : 'var(--error-color)'};
  }
  
  ${props => props.$active && `
    color: var(--error-color);
  `}
`;

const ReportButton = styled(VoteButton)`
  margin-left: auto;
  
  &:hover {
    color: var(--warning-color);
  }
  
  ${props => props.$reported && `
    color: var(--warning-color);
  `}
  
  @media (max-width: 768px) {
    padding: 4px;
    
    svg {
      margin-right: 0;
    }
  }
`;

const RemovedMessage = styled.div`
  color: rgba(255, 77, 77, 0.7);
  padding: 3px 0;
  margin: 5px 0 10px;
  font-style: italic;
  display: flex;
  align-items: center;
  transform: skewX(-3deg);
  font-weight: 300;
  font-size: 13px;
  opacity: 0.8;
  
  svg {
    margin-right: 8px;
    color: rgba(255, 77, 77, 0.7);
    font-size: 12px;
  }
`;

// Report modal components
const ReportModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(5px);
  
  @media (max-width: 768px) {
    align-items: center;
    overflow-y: auto;
  }
`;

const ReportForm = styled.div`
  background-color: rgba(18, 18, 18, 0.95);
  border-radius: 8px;
  padding: 15px;
  max-width: 320px;
  width: 90%;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  h3 {
    color: var(--text-light);
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    font-size: 16px;
    
    svg {
      margin-right: 8px;
      color: var(--warning-color);
    }
  }
  
  p {
    margin-bottom: 10px;
    color: var(--text-dim);
    font-size: 13px;
    line-height: 1.4;
  }
`;

const ReportOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 15px;
`;

const ReportOption = styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
  border-radius: 6px;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  ${props => props.$selected && `
    border-color: var(--primary-light);
    background-color: rgba(59, 130, 246, 0.1);
  `}
  
  input {
    display: none;
  }
  
  label {
    cursor: pointer;
    flex: 1;
    color: var(--text-light);
    font-size: 13px;
  }
`;

const CustomMessageField = styled.textarea`
  width: 100%;
  padding: 8px;
  border-radius: 6px;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-light);
  font-size: 13px;
  margin-top: 10px;
  resize: vertical;
  min-height: 60px;
  max-height: 120px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-light);
  }
  
  &::placeholder {
    color: var(--text-dim);
  }
`;

const ReportButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 15px;
`;

const CancelButton = styled.button`
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background-color: transparent;
  color: var(--text-light);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }
`;

const SubmitReportButton = styled.button`
  padding: 6px 12px;
  border-radius: 4px;
  border: none;
  background-color: var(--warning-color);
  color: white;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    margin-right: 6px;
    font-size: 12px;
  }
`;

const formatExactTime = (dateString) => {
  if (!dateString) return '';
  
  try {
    // Parse the UTC date string
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '';
    }
    
    // Adjust for IST (UTC+5:30)
    const istOffset = 5.5 * 60 * 60 * 1000; // 5.5 hours in milliseconds
    const istDate = new Date(date.getTime() + istOffset);
    
    // Format the date using the browser's locale settings
    return istDate.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  
  try {
    // Parse the UTC date string
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '';
    }
    
    // Adjust for IST (UTC+5:30)
    const istOffset = 5.5 * 60 * 60 * 1000; // 5.5 hours in milliseconds
    const istDate = new Date(date.getTime() + istOffset);
    
    // Calculate relative time manually to ensure accurate timezone handling
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - istDate.getTime()) / 1000);
    
    // Less than a minute
    if (diffInSeconds < 60) {
      return 'just now';
    }
    
    // Less than an hour
    if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    }
    
    // Less than a day
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    }
    
    // Less than 2 days
    if (diffInSeconds < 172800) {
      return 'yesterday';
    }
    
    // Less than a week
    if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    }
    
    // More than a week - use formatExactTime instead
    return formatExactTime(dateString);
  } catch (error) {
    console.error('Error formatting relative date:', error);
    return '';
  }
};

const CommentItem = ({ 
  comment, 
  onReply, 
  onEdit, 
  onDelete, 
  nestLevel = 0,
  contentType,
  contentId,
  refreshComments,
  replyToUsername = null,
  replyToId = null,
  onCopyLink,
  isHighlighted
}) => {
  const { currentUser, openLoginModal } = useContext(AuthContext);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showReplies, setShowReplies] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [replyError, setReplyError] = useState(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [nestedReplies, setNestedReplies] = useState([]);
  const [replyCount, setReplyCount] = useState(comment.replies?.length || 0);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showLinkWarning, setShowLinkWarning] = useState(false);
  const [warningLink, setWarningLink] = useState('');
  const [showCopyToast, setShowCopyToast] = useState(false);
  
  // Add state for feedback toast
  const [feedbackToast, setFeedbackToast] = useState({
    show: false,
    message: '',
    type: 'success'
  });
  
  // State for likes, dislikes, and reports
  const [likes, setLikes] = useState(comment.likes || 0);
  const [dislikes, setDislikes] = useState(comment.dislikes || 0);
  const [userLiked, setUserLiked] = useState(
    Array.isArray(comment.liked_by) && 
    currentUser && 
    comment.liked_by.includes(currentUser._id)
  );
  const [userDisliked, setUserDisliked] = useState(
    Array.isArray(comment.disliked_by) && 
    currentUser && 
    comment.disliked_by.includes(currentUser._id)
  );
  const [userReported, setUserReported] = useState(
    Array.isArray(comment.reported_by) && 
    currentUser && 
    comment.reported_by.includes(currentUser._id)
  );
  const [isLikeLoading, setIsLikeLoading] = useState(false);
  const [isDislikeLoading, setIsDislikeLoading] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedReportReason, setSelectedReportReason] = useState('');
  const [reportMessage, setReportMessage] = useState('');
  const [isReporting, setIsReporting] = useState(false);
  
  const navigate = useNavigate();
  
  // Check if we're on a mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  // Update replyCount when comment.replies changes
  useEffect(() => {
    setReplyCount(comment.replies?.length || 0);
  }, [comment.replies]);
  
  const formattedDate = comment.createdAt ? 
    formatExactTime(comment.createdAt) : 
    'just now';
    
  const relativeTime = comment.createdAt ?
    formatRelativeTime(comment.createdAt) :
    'just now';
  
  const isCommentOwner = currentUser && currentUser._id === comment.user_id;
  const hasNestedReplyIds = Array.isArray(comment.replies) && 
    comment.replies.length > 0 && 
    typeof comment.replies[0] === 'string';
  
  // Only allow replies to top-level comments
  const canReply = true; // Allow replies for all comments
  
  // Handler for like button
  const handleLike = async () => {
    if (!currentUser) {
      openLoginModal();
      return;
    }
    
    if (isLikeLoading || isDislikeLoading) return;
    
    setIsLikeLoading(true);
    
    try {
      const response = await CommentsAPI.likeComment(comment._id);
      
      if (response.data && response.data.success) {
        const { comment: updatedComment, action_taken } = response.data.data;
        
        // Update state based on response
        setLikes(updatedComment.likes || 0);
        setDislikes(updatedComment.dislikes || 0);
        
        // Update user action state
        if (action_taken === "added_like") {
          setUserLiked(true);
          setUserDisliked(false);
          // Show feedback toast
          setFeedbackToast({
            show: true,
            message: 'Comment liked',
            type: 'success'
          });
        } else if (action_taken === "removed_like") {
          setUserLiked(false);
          // Show feedback toast
          setFeedbackToast({
            show: true,
            message: 'Like removed',
            type: 'success'
          });
        } else if (action_taken === "removed_dislike_added_like") {
          setUserLiked(true);
          setUserDisliked(false);
          // Show feedback toast
          setFeedbackToast({
            show: true,
            message: 'Changed to like',
            type: 'success'
          });
        }
        
        // Hide toast after 3 seconds
        setTimeout(() => {
          setFeedbackToast(prev => ({ ...prev, show: false }));
        }, 3000);
      } else {
        console.error('Error liking comment:', response.data?.error || 'Unknown error');
        // Show error toast
        setFeedbackToast({
          show: true,
          message: 'Failed to like comment',
          type: 'error'
        });
        setTimeout(() => {
          setFeedbackToast(prev => ({ ...prev, show: false }));
        }, 3000);
      }
    } catch (error) {
      console.error('Error liking comment:', error);
      // Restore previous state
      setLikes(comment.likes || 0);
      setUserLiked(
        Array.isArray(comment.liked_by) && 
        currentUser && 
        comment.liked_by.includes(currentUser._id)
      );
      // Show error toast
      setFeedbackToast({
        show: true,
        message: 'Failed to like comment',
        type: 'error'
      });
      setTimeout(() => {
        setFeedbackToast(prev => ({ ...prev, show: false }));
      }, 3000);
    } finally {
      setIsLikeLoading(false);
    }
  };
  
  // Handler for dislike button
  const handleDislike = async () => {
    if (!currentUser) {
      openLoginModal();
      return;
    }
    
    if (isLikeLoading || isDislikeLoading) return;
    
    setIsDislikeLoading(true);
    
    try {
      const response = await CommentsAPI.dislikeComment(comment._id);
      
      if (response.data && response.data.success) {
        const { comment: updatedComment, action_taken } = response.data.data;
        
        // Update state based on response
        setLikes(updatedComment.likes || 0);
        setDislikes(updatedComment.dislikes || 0);
        
        // Update user action state
        if (action_taken === "added_dislike") {
          setUserDisliked(true);
          setUserLiked(false);
          // Show feedback toast
          setFeedbackToast({
            show: true,
            message: 'Comment disliked',
            type: 'success'
          });
        } else if (action_taken === "removed_dislike") {
          setUserDisliked(false);
          // Show feedback toast
          setFeedbackToast({
            show: true,
            message: 'Dislike removed',
            type: 'success'
          });
        } else if (action_taken === "removed_like_added_dislike") {
          setUserDisliked(true);
          setUserLiked(false);
          // Show feedback toast
          setFeedbackToast({
            show: true,
            message: 'Changed to dislike',
            type: 'success'
          });
        }
        
        // Hide toast after 3 seconds
        setTimeout(() => {
          setFeedbackToast(prev => ({ ...prev, show: false }));
        }, 3000);
      } else {
        console.error('Error disliking comment:', response.data?.error || 'Unknown error');
        // Show error toast
        setFeedbackToast({
          show: true,
          message: 'Failed to dislike comment',
          type: 'error'
        });
        setTimeout(() => {
          setFeedbackToast(prev => ({ ...prev, show: false }));
        }, 3000);
      }
    } catch (error) {
      console.error('Error disliking comment:', error);
      // Restore previous state
      setDislikes(comment.dislikes || 0);
      setUserDisliked(
        Array.isArray(comment.disliked_by) && 
        currentUser && 
        comment.disliked_by.includes(currentUser._id)
      );
      // Show error toast
      setFeedbackToast({
        show: true,
        message: 'Failed to dislike comment',
        type: 'error'
      });
      setTimeout(() => {
        setFeedbackToast(prev => ({ ...prev, show: false }));
      }, 3000);
    } finally {
      setIsDislikeLoading(false);
    }
  };
  
  // Handler for report button
  const openReportModal = () => {
    if (!currentUser) {
      openLoginModal();
      return;
    }
    
    if (userReported) {
      // User already reported this comment - show a message
      setFeedbackToast({
        show: true,
        message: 'You already reported this comment',
        type: 'warning'
      });
      setTimeout(() => {
        setFeedbackToast(prev => ({ ...prev, show: false }));
      }, 3000);
      return;
    }
    
    setShowReportModal(true);
  };
  
  // Handler for report submission
  const handleReportSubmit = async () => {
    if (!selectedReportReason) return;
    
    setIsReporting(true);
    
    try {
      const reportData = {
        reason: selectedReportReason,
        custom_message: reportMessage.trim() || undefined
      };
      
      const response = await CommentsAPI.reportComment(comment._id, reportData);
      
      if (response.data && response.data.success) {
        setUserReported(true);
        setShowReportModal(false);
        
        // Show success message
        setFeedbackToast({
          show: true,
          message: 'Comment reported successfully',
          type: 'success'
        });
        setTimeout(() => {
          setFeedbackToast(prev => ({ ...prev, show: false }));
        }, 3000);
        
        // Check if comment was auto-removed due to reports
        if (response.data.data.auto_removed) {
          // Handle auto-removed comment (refresh comments)
          refreshComments();
        }
      } else {
        console.error('Error reporting comment:', response.data?.error || 'Unknown error');
        // Show error toast
        setFeedbackToast({
          show: true,
          message: 'Failed to report comment',
          type: 'error'
        });
        setTimeout(() => {
          setFeedbackToast(prev => ({ ...prev, show: false }));
        }, 3000);
      }
    } catch (error) {
      console.error('Error reporting comment:', error);
      // Show error toast
      setFeedbackToast({
        show: true,
        message: 'Failed to report comment',
        type: 'error'
      });
      setTimeout(() => {
        setFeedbackToast(prev => ({ ...prev, show: false }));
      }, 3000);
    } finally {
      setIsReporting(false);
      // Reset report form
      setSelectedReportReason('');
      setReportMessage('');
    }
  };
  
  // Function to process comment content and handle links and images
  const processCommentContent = (content) => {
    if (!content) return '';
    
    // Helper function to decode HTML entities
    const decodeHTMLEntities = (text) => {
      const textArea = document.createElement('textarea');
      textArea.innerHTML = text;
      return textArea.value;
    };
    
    // Decode HTML entities in the content
    const decodedContent = decodeHTMLEntities(content);
    
    // Regular expression to find URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    
    // Regular expression to check if URL is an image
    const imageRegex = /\.(jpeg|jpg|gif|png|webp)(\?.*)?$/i;
    
    // Process emoji codes in the content
    const contentWithEmojis = processEmojis(decodedContent);
    
    // Check if the content has URLs
    if (urlRegex.test(contentWithEmojis)) {
      // Split content by URLs
      const parts = contentWithEmojis.split(urlRegex);
      const matches = contentWithEmojis.match(urlRegex) || [];
      
      return (
        <>
          {parts.map((part, i) => {
            // If this is an odd index, it's text content
            if (i % 2 === 0) {
              // Render part with emojis
              return <span dangerouslySetInnerHTML={{ __html: part }} />;
            } else {
              const url = matches[Math.floor(i / 2)];
              const isImage = imageRegex.test(url);
              const isExternalLink = !url.includes(window.location.hostname);
              
              // If it's an image URL, render the image
              if (isImage) {
                return (
                  <EmbeddedImage key={i}>
                    <img src={url} alt="User shared content" loading="lazy" />
                    <div className="image-caption">
                      <FontAwesomeIcon icon={faExternalLinkAlt} />
                      Image from:
                      <a 
                        href="#" 
                        onClick={(e) => handleLinkClick(e, url)}
                        title={url}
                      >
                        {url.length > 30 ? url.substring(0, 30) + '...' : url}
                      </a>
                    </div>
                  </EmbeddedImage>
                );
              }
              
              // Otherwise, render as a link
              return (
                <a 
                  key={i}
                  href="#" 
                  onClick={(e) => handleLinkClick(e, url)}
                  className={isExternalLink ? "external-link" : ""}
                >
                  {url.length > 30 ? url.substring(0, 30) + '...' : url}
                </a>
              );
            }
          })}
        </>
      );
    }
    
    // If no URLs, just return the content with emojis
    return <span dangerouslySetInnerHTML={{ __html: contentWithEmojis }} />;
  };
  
  // Handle external link clicks
  const handleLinkClick = (e, url) => {
    e.preventDefault();
    
    // Check if it's an external link
    if (!url.includes(window.location.hostname)) {
      setWarningLink(url);
      setShowLinkWarning(true);
    } else {
      // It's an internal link, navigate directly
      window.open(url, '_blank');
    }
  };
  
  // Handle proceeding to external link
  const handleProceedToLink = () => {
    if (warningLink) {
      window.open(warningLink, '_blank');
    }
    setShowLinkWarning(false);
  };
  
  // Make sure comment.content is a string
  const commentContent = (() => {
    // If it's an object with a content property
    if (typeof comment.content === 'object' && comment.content !== null) {
      return comment.content.content || JSON.stringify(comment.content);
    }
    
    // If it's a string that might be a JSON object
    if (typeof comment.content === 'string') {
      try {
        const parsedContent = JSON.parse(comment.content);
        if (parsedContent && typeof parsedContent === 'object' && parsedContent.content) {
          return parsedContent.content;
        }
      } catch (e) {
        // It's just a regular string, not JSON
      }
    }
    
    // Default case
    return comment.content || '';
  })();

  // Get the username for @ mentions in replies
  const usernameForReply = comment.username || 'Anonymous';
  
  // Fetch nested replies if they're just IDs
  useEffect(() => {
    const fetchNestedReplies = async () => {
      if (hasNestedReplyIds && showReplies) {
        setLoadingReplies(true);
        try {
          const replies = await CommentsAPI.fetchNestedReplies(comment.replies);
          setNestedReplies(replies);
          // Update the reply count with the actual number of replies
          setReplyCount(replies.length);
        } catch (error) {
          console.error('Error fetching nested replies:', error);
        } finally {
          setLoadingReplies(false);
        }
      }
    };
    
    fetchNestedReplies();
  }, [comment.replies, hasNestedReplyIds, showReplies]);
  
  // Determine if the comment has replies
  const hasReplies = replyCount > 0;
  
  // Get the actual replies to render
  const repliesToRender = hasNestedReplyIds ? nestedReplies : comment.replies;
  
  // Update reply count when nestedReplies changes
  useEffect(() => {
    if (hasNestedReplyIds && nestedReplies) {
      setReplyCount(nestedReplies.length);
    }
  }, [nestedReplies, hasNestedReplyIds]);
  
  const handleReplyClick = () => {
    setReplyError(null);
    setShowReplyForm(!showReplyForm);
    setShowReplies(true);
    setShowMobileMenu(false);
  };
  
  const handleReplySubmit = async (replyContent) => {
    try {
      setReplyError(null);
      // Add @username to the beginning of the reply if it's not already there
      const formattedReply = replyContent.startsWith(`@${usernameForReply}`) ? 
        replyContent : 
        `@${usernameForReply} ${replyContent}`;
      
      await onReply(comment._id, formattedReply);
      setShowReplyForm(false);
      // Force refresh comments to ensure replies are properly displayed
      if (nestLevel > 0) {
        refreshComments();
      }
    } catch (error) {
      console.error('Error in handleReplySubmit:', error);
      setReplyError(error.message || 'Failed to submit reply. Please try again.');
      // Auto-hide error after 5 seconds
      setTimeout(() => setReplyError(null), 5000);
    }
  };
  
  const handleEdit = async (newContent) => {
    try {
      setIsEditing(true);
      await onEdit(comment._id, newContent);
      setIsEditing(false);
      setShowMobileMenu(false);
    } catch (error) {
      console.error('Error editing comment:', error);
      // Show error message
      const commentElement = document.getElementById(`comment-${comment._id}`);
      if (commentElement) {
        const errorDiv = document.createElement('div');
        errorDiv.className = 'edit-error';
        errorDiv.innerHTML = `<span style="color: #ff4d4d; font-size: 13px;">
          <i class="fas fa-exclamation-circle"></i> ${error.message || 'Failed to edit comment'}
        </span>`;
        
        commentElement.appendChild(errorDiv);
        
        // Remove after 5 seconds
        setTimeout(() => {
          const errorElement = commentElement.querySelector('.edit-error');
          if (errorElement) errorElement.remove();
        }, 5000);
      }
      setIsEditing(false);
    }
  };
  
  const handleDelete = async () => {
    // Remove confirmation dialog
    setIsDeleting(true);
    
    // Add deleting class for smooth animation
    const commentContainer = document.getElementById(`comment-${comment._id}`);
    if (commentContainer) {
      commentContainer.classList.add('deleting');
    }
    
    try {
      // Delay the actual deletion slightly to allow animation to play
      setTimeout(async () => {
        try {
          await onDelete(comment._id);
          
          // If this is a nested reply being deleted, update the parent's nestedReplies
          if (nestLevel > 0 && replyToId) {
            // Find the parent comment element
            const parentElement = document.getElementById(`comment-${replyToId}`);
            if (parentElement) {
              // Force refresh of the parent to update reply count
              refreshComments();
            }
          }
        } catch (error) {
          console.error('Error deleting comment:', error);
          // Remove the deleting class if there's an error
          if (commentContainer) {
            commentContainer.classList.remove('deleting');
            
            // Show error notification
            const errorDiv = document.createElement('div');
            errorDiv.className = 'delete-error';
            errorDiv.style.cssText = 'color: #ff4d4d; font-size: 13px; margin-top: 10px; padding: 8px; background-color: rgba(220, 38, 38, 0.1); border-radius: 8px;';
            errorDiv.innerHTML = `<i class="fas fa-exclamation-circle"></i> ${error.message || 'Failed to delete comment'}`;
            
            commentContainer.appendChild(errorDiv);
            
            // Remove after 5 seconds
            setTimeout(() => {
              const errorElement = commentContainer.querySelector('.delete-error');
              if (errorElement) errorElement.remove();
            }, 5000);
          }
          setIsDeleting(false);
        }
      }, 300);
    } catch (error) {
      console.error('Error deleting comment:', error);
      // Remove the deleting class if there's an error
      if (commentContainer) {
        commentContainer.classList.remove('deleting');
      }
      setIsDeleting(false);
    }
  };
  
  const toggleReplies = () => {
    setShowReplies(!showReplies);
  };
  
  const handleProfileClick = () => {
    if (comment.username) {
      navigate(`/profile/${comment.username}`);
    }
  };
  
  const toggleMobileMenu = (e) => {
    e.stopPropagation();
    setShowMobileMenu(!showMobileMenu);
  };
  
  // Add click outside handler for mobile menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if we clicked outside the mobile menu
      if (showMobileMenu) {
        const mobileMenu = document.querySelector(`#mobile-menu-${comment._id}`);
        const mobileMenuButton = document.querySelector(`#mobile-menu-button-${comment._id}`);
        
        if (mobileMenu && !mobileMenu.contains(event.target) && 
            mobileMenuButton && !mobileMenuButton.contains(event.target)) {
          setShowMobileMenu(false);
        }
      }
    };
    
    if (showMobileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showMobileMenu, comment._id]);
  
  const handleReplyUserClick = (username) => {
    if (username) {
      navigate(`/profile/${username}`);
    }
  };
  
  // Inside the CommentItem component, add a check for system comments
  const isSystemComment = comment.user_id === 'system';
  
  // Handle copy link to comment
  const handleCopyLink = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (onCopyLink && comment._id) {
      onCopyLink(comment._id);
      setShowCopyToast(true);
      setTimeout(() => setShowCopyToast(false), 3000);
    }
  };
  
  return (
    <CommentContainer id={`comment-${comment._id}`} className={isHighlighted ? 'highlighted' : ''}>
      {replyToUsername && (
        <ReplyChain>
          <span onClick={() => handleReplyUserClick(replyToUsername)}>@{replyToUsername}</span>
          <FontAwesomeIcon icon={faReply} />
          <span onClick={() => handleProfileClick()}>{comment.username || 'Anonymous'}</span>
        </ReplyChain>
      )}
      
      <CommentHeader>
        <Avatar onClick={handleProfileClick}>
          {comment.avatar ? (
            <img src={`/avatars/${comment.avatar}`} alt={`${comment.username || 'Anonymous'}'s avatar`} />
          ) : (
            <FontAwesomeIcon icon={faUser} />
          )}
        </Avatar>
        <CommentMeta>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Username onClick={handleProfileClick}>
              {comment.username || 'Anonymous'}
            </Username>
            
            {/* Show pinned badge for system comments */}
            {comment.username === 'System' && (
              <PinnedBadge>
                <FontAwesomeIcon icon={faThumbtack} />
                Pinned
              </PinnedBadge>
            )}
            
            {/* Show moderator badge for username 'flyhigh' */}
            {comment.username && comment.username.toLowerCase() === 'flyhigh' && (
              <ModeratorBadge>
                <FontAwesomeIcon icon={faShieldAlt} />
                Mod
              </ModeratorBadge>
            )}
          </div>
          <Timestamp title={formattedDate}>
            {relativeTime}
          </Timestamp>
        </CommentMeta>
        
        {isCommentOwner && !isSystemComment && (
          <MobileActionsButton 
            onClick={toggleMobileMenu}
            id={`mobile-menu-button-${comment._id}`}
          >
            <FontAwesomeIcon icon={faEllipsisV} />
          </MobileActionsButton>
        )}
        
        {showMobileMenu && !isSystemComment && (
          <MobileActionsMenu id={`mobile-menu-${comment._id}`}>
            {isCommentOwner && (
              <button onClick={() => setIsEditing(true)}>
                <FontAwesomeIcon icon={faEdit} /> Edit
              </button>
            )}
            
            {/* Share button */}
            {nestLevel === 0 && (
              <button onClick={handleCopyLink}>
                <FontAwesomeIcon icon={faLink} /> Share
              </button>
            )}
            
            {/* Report options */}
            {currentUser && !isCommentOwner && !userReported && (
              <button onClick={openReportModal}>
                <FontAwesomeIcon icon={faFlag} /> Report
              </button>
            )}
            
            {currentUser && !isCommentOwner && userReported && (
              <button disabled style={{ opacity: 0.6 }}>
                <FontAwesomeIcon icon={faFlag} /> Reported
              </button>
            )}
          </MobileActionsMenu>
        )}
      </CommentHeader>
      
      {isEditing && !isMobile ? (
        // On desktop, use the inline editor
        <CommentForm
          onSubmit={handleEdit}
          onCancel={() => setIsEditing(false)}
          initialValue={commentContent}
          buttonText="Save"
          hideGuidelines={true}
        />
      ) : (
        <>
          {comment.is_removed ? (
            <RemovedMessage>
              <FontAwesomeIcon icon={faExclamationTriangle} />
              Comment removed
            </RemovedMessage>
          ) : (
            <Content>{processCommentContent(commentContent)}</Content>
          )}
          
          {/* Combined action buttons */}
          {!isSystemComment && !comment.is_removed && (
            <VoteContainer>
              {/* Like and Dislike buttons */}
              <LikeButton 
                onClick={handleLike} 
                $active={userLiked}
                disabled={isLikeLoading || isDislikeLoading}
                title={userLiked ? "Remove like" : "Like this comment"}
              >
                {isLikeLoading ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <FontAwesomeIcon icon={faThumbsUp} />
                )}
                {likes > 0 && likes}
              </LikeButton>
              
              <DislikeButton 
                onClick={handleDislike} 
                $active={userDisliked}
                disabled={isLikeLoading || isDislikeLoading}
                title={userDisliked ? "Remove dislike" : "Dislike this comment"}
              >
                {isDislikeLoading ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <FontAwesomeIcon icon={faThumbsDown} />
                )}
                {dislikes > 0 && dislikes}
              </DislikeButton>
              
              {/* Other action buttons */}
              <Actions>
                {canReply && (
                  <ActionButton onClick={handleReplyClick} title="Reply to this comment">
                    <FontAwesomeIcon icon={faReply} />
                    <span className="button-text">Reply</span>
                  </ActionButton>
                )}
                
                {isCommentOwner && (
                  <ActionButton className="delete" onClick={handleDelete} disabled={isDeleting} title="Delete your comment">
                    {isDeleting ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      <FontAwesomeIcon icon={faTrash} />
                    )}
                    <span className="button-text">Delete</span>
                  </ActionButton>
                )}
                
                {/* More options button that shows on mobile */}
                {(isCommentOwner || (currentUser && !isCommentOwner) || nestLevel === 0) && (
                  <ActionButton 
                    onClick={toggleMobileMenu} 
                    title="More options"
                    id={`mobile-menu-button-${comment._id}`}
                  >
                    <FontAwesomeIcon icon={faEllipsisV} />
                  </ActionButton>
                )}
              </Actions>
              
              {/* Report button moved to the far right */}
              {currentUser && !isCommentOwner && (
                <ReportButton 
                  onClick={openReportModal} 
                  $reported={userReported}
                  title={userReported ? "You reported this comment" : "Report this comment"}
                  style={{ marginLeft: 'auto' }}
                >
                  <FontAwesomeIcon icon={faFlag} />
                </ReportButton>
              )}
            </VoteContainer>
          )}
        </>
      )}
      
      {replyError && (
        <ErrorMessage>
          <FontAwesomeIcon icon={faExclamationCircle} />
          {replyError}
        </ErrorMessage>
      )}
      
      {/* External Link Warning Modal */}
      {showLinkWarning && (
        <ModalOverlay>
          <ModalContent>
            <h3>
              <FontAwesomeIcon icon={faExclamationTriangle} />
              External Link Warning
            </h3>
            <p>You are about to visit an external website. We are not responsible for the content of external sites.</p>
            <div className="link-display">{warningLink}</div>
            <div className="button-group">
              <button className="cancel" onClick={() => setShowLinkWarning(false)}>Cancel</button>
              <button className="proceed" onClick={handleProceedToLink}>Proceed</button>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}
      
      {/* Report Comment Modal */}
      {showReportModal && (
        <ReportModal 
          onClick={() => !isReporting && setShowReportModal(false)}
          id={`report-modal-${comment._id}`}
        >
          <ReportForm 
            onClick={(e) => e.stopPropagation()}
            id={`report-form-${comment._id}`}>
            <h3>
              <FontAwesomeIcon icon={faFlag} />
              Report Comment
            </h3>
            <p>Select a reason:</p>
            
            <ReportOptions>
              {['spam', 'harassment', 'hate_speech', 'misinformation', 'inappropriate', 'violence', 'other'].map(reason => (
                <ReportOption 
                  key={reason} 
                  $selected={selectedReportReason === reason}
                  onClick={() => !isReporting && setSelectedReportReason(reason)}
                >
                  <input 
                    type="radio" 
                    id={`reason-${reason}`} 
                    name="report-reason" 
                    value={reason} 
                    checked={selectedReportReason === reason}
                    onChange={() => setSelectedReportReason(reason)}
                  />
                  <label htmlFor={`reason-${reason}`}>
                    {reason.charAt(0).toUpperCase() + reason.slice(1).replace('_', ' ')}
                  </label>
                </ReportOption>
              ))}
            </ReportOptions>
            
            {selectedReportReason && (
              <CustomMessageField 
                placeholder="Additional details (optional)"
                value={reportMessage}
                onChange={(e) => setReportMessage(e.target.value)}
                disabled={isReporting}
              />
            )}
            
            <ReportButtonGroup>
              <CancelButton 
                type="button" 
                onClick={() => setShowReportModal(false)}
                disabled={isReporting}
              >
                Cancel
              </CancelButton>
              <SubmitReportButton 
                type="button" 
                onClick={handleReportSubmit}
                disabled={!selectedReportReason || isReporting}
              >
                {isReporting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Submitting...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faFlag} />
                    Report
                  </>
                )}
              </SubmitReportButton>
            </ReportButtonGroup>
          </ReportForm>
        </ReportModal>
      )}
      
      {/* For mobile, show the edit form in the same container as replies */}
      {isEditing && isMobile && (
        <ReplyFormContainer>
          <CommentForm
            onSubmit={handleEdit}
            onCancel={() => setIsEditing(false)}
            initialValue={commentContent}
            buttonText="Save"
            hideGuidelines={true}
          />
        </ReplyFormContainer>
      )}
      
      {showReplyForm && (
        <ReplyFormContainer>
          <ReplyingTo>
            Replying to <span>@{usernameForReply}</span>
          </ReplyingTo>
          <CommentForm
            onSubmit={handleReplySubmit}
            onCancel={() => setShowReplyForm(false)}
            placeholder={`Reply to @${usernameForReply}...`}
            buttonText="Reply"
            hideGuidelines={true}
          />
          {replyError && (
            <div style={{ color: 'var(--error-color)', marginTop: '5px', fontSize: '13px' }}>
              {replyError}
            </div>
          )}
        </ReplyFormContainer>
      )}
      
      {hasReplies && (
        <ToggleRepliesButton onClick={toggleReplies}>
          <FontAwesomeIcon icon={showReplies ? faChevronUp : faChevronDown} />
          {showReplies ? 'Hide' : 'Show'} Replies
          <ReplyCount>{replyCount}</ReplyCount>
        </ToggleRepliesButton>
      )}
      
      {hasReplies && showReplies && (
        <RepliesContainer>
          {loadingReplies ? (
            <div style={{ padding: '10px', color: 'var(--text-dim)', textAlign: 'center' }}>
              <FontAwesomeIcon icon={faSpinner} spin /> Loading replies...
            </div>
          ) : (
            repliesToRender.map(reply => {
              if (nestLevel === 0) {
                return (
                  <CommentItem
                    key={reply._id}
                    comment={reply}
                    onReply={onReply}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    nestLevel={nestLevel + 1}
                    contentType={contentType}
                    contentId={contentId}
                    refreshComments={refreshComments}
                    replyToUsername={usernameForReply}
                    replyToId={comment._id}
                    onCopyLink={onCopyLink}
                  />
                );
              } else {
                // Check if this reply has nested replies
                const replyHasNestedReplies = 
                  Array.isArray(reply.replies) && 
                  reply.replies.length > 0;
                  
                if (replyHasNestedReplies) {
                  // For flattened view, just show the direct reply
                  return (
                    <CommentItem
                      key={reply._id}
                      comment={reply}
                      onReply={onReply}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      nestLevel={nestLevel + 1}
                      contentType={contentType}
                      contentId={contentId}
                      refreshComments={refreshComments}
                      replyToUsername={usernameForReply}
                      replyToId={comment._id}
                      onCopyLink={onCopyLink}
                    />
                  );
                } else {
                  return (
                    <CommentItem
                      key={reply._id}
                      comment={reply}
                      onReply={onReply}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      nestLevel={nestLevel + 1}
                      contentType={contentType}
                      contentId={contentId}
                      refreshComments={refreshComments}
                      replyToUsername={usernameForReply}
                      replyToId={comment._id}
                      onCopyLink={onCopyLink}
                    />
                  );
                }
              }
            })
          )}
        </RepliesContainer>
      )}
      
      {/* Show toast when link is copied */}
      {showCopyToast && (
        <CopyLinkToast>
          <FontAwesomeIcon icon={faCheck} /> Link copied to clipboard
        </CopyLinkToast>
      )}
      
      {/* Show feedback toast for likes, dislikes, reports */}
      {feedbackToast.show && (
        <FeedbackToast $type={feedbackToast.type}>
          <FontAwesomeIcon 
            icon={
              feedbackToast.type === 'success' ? faCheck : 
              feedbackToast.type === 'warning' ? faExclamationTriangle : 
              faExclamationCircle
            } 
          /> 
          {feedbackToast.message}
        </FeedbackToast>
      )}
    </CommentContainer>
  );
};

export default CommentItem; 